// src/pages/Rankings.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchRankings,
} from "../Api";

/* =========================
   Helpers
========================= */

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatTimeTR(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } catch {
    return "-";
  }
}

function formatRemaining(seconds) {
  const s = Math.max(0, Number(seconds || 0));
  const days = Math.floor(s / 86400);
  const rem1 = s % 86400;
  const hours = Math.floor(rem1 / 3600);
  const rem2 = rem1 % 3600;
  const mins = Math.floor(rem2 / 60);
  const secs = rem2 % 60;
  return `${days}g ${hours}s ${mins}d ${secs}sn`;
}

/**
 * ✅ Sende gelen format:
 *   - "Soyad, Adlar"  -> "Adlar Soyad"
 *   - "Soyad Adlar"   -> "Adlar Soyad" (virgülsüz de soyad başta geliyor)
 */
function normalizeNameOrder(fullName) {
  const raw = (fullName || "").trim().replace(/\s+/g, " ");
  if (!raw) return "";

  if (raw.includes(",")) {
    const parts = raw
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const lastName = parts[0];
      const firstNames = parts.slice(1).join(" ");
      return `${firstNames} ${lastName}`.trim();
    }
    return raw.replace(",", " ").trim();
  }

  const words = raw.split(" ").filter(Boolean);
  if (words.length <= 1) return raw;

  const lastName = words[0];
  const firstNames = words.slice(1).join(" ");
  return `${firstNames} ${lastName}`.trim();
}

// Her kelime: İlk harf + ***
function maskName(fullName) {
  const fixed = normalizeNameOrder(fullName);
  if (!fixed) return "-";

  const parts = fixed.split(" ").filter(Boolean);
  if (!parts.length) return "-";

  return parts
    .map((w) => {
      const ch = (w[0] || "").toUpperCase();
      return ch ? `${ch}***` : "";
    })
    .filter(Boolean)
    .join(" ");
}

function buildWindowedList(items, topN = 10) {
  const arr = Array.isArray(items) ? items : [];
  return arr.slice(0, topN).map((x) => ({ type: "item", ...x }));
}

/* =========================
   Prize helper (örnek)
========================= */
function prizeByRank(rank) {
  const r = Number(rank || 0);
  if (r === 1) return "1.000₺";
  if (r === 2) return "750₺";
  if (r === 3) return "500₺";
  if (r >= 4 && r <= 10) return "250₺";
  if (r >= 11 && r <= 20) return "100₺";
  return "—";
}

/* =========================
   "Yeni ay başladı" banner mantığı (1 gün)
========================= */

function msDay() {
  return 24 * 60 * 60 * 1000;
}

function parseDateMs(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

function isWithinFirstDay(endMs) {
  if (!endMs) return false;
  const now = Date.now();
  return now >= endMs && now < endMs + msDay();
}

function safeNum(x, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

/* =========================
   Component
========================= */

export default function Rankings() {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [tab, setTab] = useState("current"); // "current" | "previous"

  const pollTimerRef = useRef(null);

  // ✅ canlı kalan süre
  const [remainSecLive, setRemainSecLive] = useState(null);

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  const meta = data?.meta || {};
  const itemsCurrent = useMemo(() => data?.current?.items || [], [data]);
  const itemsPrev = useMemo(() => data?.previous?.items || [], [data]);

  const meCurrent = data?.me?.current || null;
  const mePrev = data?.me?.previous || null;

  const lastCampaign = meta?.last_campaign || null;
  const winners = useMemo(() => lastCampaign?.winners_top20 || [], [lastCampaign]);

  // ✅ login olmuş sürücünün id'si
  const myDriverId =
    data?.me?.driver_id ||
    (typeof window !== "undefined" && window.__me_driver_id) ||
    "";

  // ✅ giriş yapan kişinin adı soyadı (maskesiz gösterilecek)
  const myProfileNameRaw =
    data?.me?.profile?.first_name || data?.me?.profile?.last_name
      ? `${data?.me?.profile?.first_name || ""} ${
          data?.me?.profile?.last_name || ""
        }`.trim()
      : data?.me?.profile?.full_name ||
        data?.me?.profile?.name ||
        meCurrent?.name ||
        "";

  const myDisplayName = normalizeNameOrder(myProfileNameRaw);

  const visibleCurrent = useMemo(
    () => buildWindowedList(itemsCurrent, 10),
    [itemsCurrent]
  );
  const visiblePrev = useMemo(
    () => buildWindowedList(itemsPrev, 10),
    [itemsPrev]
  );

  // ✅ geçtiğimiz kampanyada ilk 20'de miydi?
  const myWinnerInfo = useMemo(() => {
    if (!myDriverId) return null;
    const hit = (winners || []).find(
      (x) => String(x?.driver_id || "") === String(myDriverId)
    );
    return hit || null;
  }, [winners, myDriverId]);

  // ✅ Banner görünürlüğü (1 gün)
  const showNewCampaignBanner = useMemo(() => {
    const endIso =
      lastCampaign?.end_utc ||
      lastCampaign?.end_utc_iso ||
      lastCampaign?.ended_at_utc ||
      lastCampaign?.ended_at ||
      "";

    const endMsFromBackend = parseDateMs(endIso);

    const endMsFromLS = safeNum(
      localStorage.getItem("sitaxi_last_campaign_end_ms"),
      0
    );

    const endMs = endMsFromBackend || endMsFromLS || 0;
    return isWithinFirstDay(endMs);
  }, [lastCampaign]);

  // ✅ Kazananın kaçıncı olduğunu banner’da göstermek için
  function getMyRankForBanner() {
    if (!myWinnerInfo) return null;
    const r1 = safeNum(myWinnerInfo.rank, 0);
    if (r1 > 0) return r1;

    const idx = (winners || []).findIndex(
      (x) => String(x?.driver_id || "") === String(myDriverId)
    );
    return idx >= 0 ? idx + 1 : null;
  }

  const myRankLast = getMyRankForBanner();

  async function load() {
    setError("");
    setLoading(true);
    try {
      const res = await fetchRankings(token);
      setData(res);

      const sec = res?.meta?.campaign_remaining_seconds;
      setRemainSecLive(typeof sec === "number" ? sec : Number(sec || 0));
    } catch (e) {
      setError(e?.message || "Sıralama alınamadı.");
      setData(null);
      setRemainSecLive(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const pollTimer = pollTimerRef.current;
    return () => {
      if (pollTimer) clearInterval(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ canlı countdown (her saniye)
  useEffect(() => {
    if (remainSecLive === null || remainSecLive === undefined) return;

    const t = setInterval(() => {
      setRemainSecLive((s) => {
        const n = Number(s || 0);
        return n > 0 ? n - 1 : 0;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [remainSecLive]);

  const list = tab === "current" ? visibleCurrent : visiblePrev;

  const myRankCurrent = meCurrent?.rank ?? null;
  const myCountCurrent = meCurrent?.count ?? null;

  const myRankPrev = mePrev?.rank ?? null;
  const myCountPrev = mePrev?.count ?? null;

  const remainText = formatRemaining(
    remainSecLive ?? meta?.campaign_remaining_seconds
  );

  return (
    <div style={styles.page}>
      {error ? <div style={styles.errorBox}>{error}</div> : null}

      {/* ✅ 1 gün boyunca: Geçen ay kazandı/kazanamadı bilgisi */}
      {showNewCampaignBanner ? (
        <div
          style={{
            ...styles.bannerBox,
            background: myWinnerInfo ? "#ecfdf5" : "#eef2ff",
            borderColor: myWinnerInfo ? "#bbf7d0" : "#e0e7ff",
            color: "#111827",
          }}
        >
          {myWinnerInfo ? (
            <>
              <div style={styles.bannerTitle}>🎉 Tebrikler, kazandınız!</div>
              <div style={styles.bannerText}>
                Geçtiğimiz ayı{" "}
                <b>{myRankLast != null ? `${myRankLast}.` : "—"}</b> sırada
                bitirdiniz. Kazancınız:{" "}
                <b>{myRankLast != null ? prizeByRank(myRankLast) : "—"}</b>
              </div>
              <div style={styles.bannerSub}>
                Not: Bu bilgilendirme yeni kampanyanın ilk günü boyunca görünür.
              </div>
            </>
          ) : (
            <>
              <div style={styles.bannerTitle}>🚀 Yeni ay başladı!</div>
              <div style={styles.bannerText}>
                Bol şans! Bu ay ilk 20’ye girerek ödül kazanabilirsiniz.
              </div>
              <div style={styles.bannerSub}>
                Not: Bu bilgilendirme yeni kampanyanın ilk günü boyunca görünür.
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* “Sizin pozisyonunuz” kartı */}
      <div style={styles.positionCard}>
        <div style={styles.positionTop}>
          <div>
            <div style={styles.positionTitle}>Sizin pozisyonunuz</div>

            {/* ✅ MASKESİZ isim soyisim */}
            <div style={styles.positionName}>
              {loading ? "…" : myDisplayName || "-"}
            </div>
          </div>

          <div style={styles.positionCols}>
            <div style={styles.positionCol}>
              <div style={styles.positionLabel}>Bugün</div>
              <div style={styles.positionValue}>
                {loading ? "…" : myRankCurrent ?? "–"}
              </div>
              <div style={styles.positionSub}>
                {loading
                  ? ""
                  : myCountCurrent != null
                  ? `${myCountCurrent} sipariş`
                  : ""}
              </div>
            </div>

            <div style={styles.positionCol}>
              <div style={styles.positionLabel}>Dün</div>
              <div style={styles.positionValue}>
                {loading ? "…" : myRankPrev ?? "–"}
              </div>
              <div style={styles.positionSub}>
                {loading
                  ? ""
                  : myCountPrev != null
                  ? `${myCountPrev} sipariş`
                  : ""}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.positionBottom}>
          Sonuçların açıklanmasına kalan süre: <b>{remainText}</b>
        </div>
      </div>

      {/* ✅ Yeni kampanyanın ilk günü: Geçtiğimiz ay ilk 20 tablosu */}
      {showNewCampaignBanner && winners.length ? (
        <div style={{ ...styles.card, marginBottom: 18 }}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitle}>Geçtiğimiz Ay İlk 20</div>
            <div style={{ fontWeight: 800, color: "#6b7280" }}>
              {lastCampaign?.start_date || "-"} → {lastCampaign?.end_date || "-"}
            </div>
          </div>

          <div style={styles.tableHead}>
            <div style={{ ...styles.th, width: 90 }}>Sıra</div>
            <div style={{ ...styles.th, flex: 1 }}>Sürücü</div>
            <div style={{ ...styles.th, width: 160, textAlign: "right" }}>
              Kazanç
            </div>
          </div>

          <div>
            {winners.map((w, i) => {
              const r = safeNum(w.rank, 0) > 0 ? safeNum(w.rank, 0) : i + 1;
              const nameMasked = maskName(w.name);
              const prize = prizeByRank(r);

              return (
                <div key={w.driver_id || i} style={styles.tr}>
                  <div style={{ ...styles.td, width: 90 }}>
                    <span style={styles.rankPill}>{r}</span>
                  </div>

                  <div style={{ ...styles.td, flex: 1 }}>
                    <span style={styles.driverName}>{nameMasked}</span>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        fontWeight: 700,
                        marginTop: 4,
                      }}
                    >
                      {w.count ?? 0} sipariş
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.td,
                      width: 160,
                      textAlign: "right",
                      fontWeight: 900,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {prize}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding: 14, color: "#6b7280", fontWeight: 700 }}>
            Bu liste, kampanya bittiği an “son çekilen” sıralamadan üretilir ve
            yeni kampanyanın ilk günü boyunca görünür.
          </div>
        </div>
      ) : null}

      {/* Sıralama Tablosu */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitle}>Sıralama Tablosu</div>

          <div style={styles.tabs}>
            <button
              onClick={() => setTab("current")}
              style={{
                ...styles.tabBtn,
                ...(tab === "current" ? styles.tabBtnActive : {}),
              }}
            >
              Mevcut
            </button>
            <button
              onClick={() => setTab("previous")}
              style={{
                ...styles.tabBtn,
                ...(tab === "previous" ? styles.tabBtnActive : {}),
              }}
            >
              Dün
            </button>
          </div>
        </div>

        <div style={styles.tableHead}>
          <div style={{ ...styles.th, width: 90 }}>Sıra</div>
          <div style={{ ...styles.th, flex: 1 }}>Sürücü</div>
          <div style={{ ...styles.th, width: 140, textAlign: "right" }}>
            Siparişler
          </div>
        </div>

        <div>
          {loading ? (
            <div style={styles.loading}>Yükleniyor...</div>
          ) : (
            <>
              {list.map((row, idx) => {
                if (row.type === "gap") {
                  return (
                    <div key="gap" style={styles.gapRow}>
                      <div style={styles.gapDots}>…</div>
                    </div>
                  );
                }

                const masked = maskName(row.name);
                // ✅ SENİ TABLODA BELİRGİNLEŞTİR
                const isMe =
                  myDriverId && String(row.driver_id) === String(myDriverId);

                return (
                  <div
                    key={`${row.driver_id || "x"}_${row.rank || idx}`}
                    style={{
                      ...styles.tr,
                      ...(isMe ? styles.trMe : {}),
                    }}
                  >
                    <div style={{ ...styles.td, width: 90 }}>
                      <span style={styles.rankPill}>{row.rank}</span>
                    </div>

                    <div style={{ ...styles.td, flex: 1 }}>
                      <span style={styles.driverName}>{masked}</span>
                      {isMe ? (
                        <span style={styles.meBadge}>Siz</span>
                      ) : null}
                    </div>

                    <div
                      style={{
                        ...styles.td,
                        width: 140,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.count ?? 0}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <div style={styles.infoBar}>
        <span style={styles.infoIcon}>ⓘ</span>
        <span>
          Sıralama günde bir kez güncellenir. Son güncelleme:{" "}
          <b>{formatTimeTR(meta?.updated_at_tr)}</b>
        </span>
      </div>

    </div>
  );
}

/* =========================
   Styles
========================= */

const styles = {
  page: {
    padding: 24,
    maxWidth: 1200,
  },

  errorBox: {
    marginBottom: 14,
    background: "#fee2e2",
    border: "1px solid #fecaca",
    color: "#7f1d1d",
    padding: 12,
    borderRadius: 12,
    fontWeight: 700,
  },

  bannerBox: {
    marginBottom: 14,
    padding: 14,
    borderRadius: 12,
    border: "1px solid",
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: 900,
    marginBottom: 6,
  },
  bannerText: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.35,
  },
  bannerSub: {
    marginTop: 6,
    fontSize: 12.5,
    color: "#6b7280",
    fontWeight: 800,
  },

  positionCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 18,
    marginBottom: 18,
  },
  positionTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  positionTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: "#111827",
  },
  positionName: {
    marginTop: 6,
    fontSize: 14,
    fontWeight: 800,
    color: "#111827",
    letterSpacing: 0.2,
  },

  positionCols: {
    display: "flex",
    gap: 80,
    alignItems: "flex-start",
    justifyContent: "flex-end",
    minWidth: 360,
  },
  positionCol: {
    textAlign: "center",
    minWidth: 120,
  },
  positionLabel: {
    fontSize: 14,
    color: "#6b7280",
    fontWeight: 700,
    marginBottom: 6,
  },
  positionValue: {
    fontSize: 20,
    fontWeight: 900,
    color: "#111827",
    lineHeight: "24px",
  },
  positionSub: {
    marginTop: 4,
    fontSize: 12,
    color: "#6b7280",
    fontWeight: 700,
  },
  positionBottom: {
    marginTop: 18,
    color: "#6b7280",
    fontWeight: 700,
  },

  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 16px 10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: "#111827",
  },

  tabs: {
    display: "inline-flex",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 4,
    gap: 4,
  },
  tabBtn: {
    border: "none",
    background: "transparent",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 800,
    color: "#111827",
  },
  tabBtnActive: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },

  tableHead: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderTop: "1px solid #f1f5f9",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
  },
  th: {
    fontWeight: 900,
    color: "#111827",
  },

  tr: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
  trMe: {
    background: "#fff7ed",
    outline: "1px solid #fdba74",
  },

  td: {
    color: "#111827",
  },

  rankPill: {
    display: "inline-flex",
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "#fde68a",
    fontWeight: 900,
  },

  driverName: {
    fontWeight: 800,
    letterSpacing: 0.2,
  },

  meBadge: {
    marginLeft: 10,
    padding: "4px 8px",
    borderRadius: 999,
    background: "#111827",
    color: "#fff",
    fontSize: 12,
    fontWeight: 900,
  },

  gapRow: {
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "center",
  },
  gapDots: {
    color: "#6b7280",
    fontSize: 22,
    lineHeight: "22px",
  },

  loading: {
    padding: 16,
    color: "#6b7280",
    fontWeight: 800,
  },

  infoBar: {
    marginTop: 14,
    padding: "12px 14px",
    borderRadius: 12,
    background: "#eef2ff",
    border: "1px solid #e0e7ff",
    color: "#1f2937",
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 700,
  },
  infoIcon: {
    display: "inline-flex",
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "#e0e7ff",
    fontWeight: 900,
  },

};
